import { API_BASE_URL } from '../utils/apiConfig';

class ForumService {
  // Get all forum posts
  static async getPosts(category = null, sort = 'newest', page = 1, limit = 20) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      const params = new URLSearchParams();
      
      if (category) params.append('category', category);
      params.append('sort', sort);
      params.append('page', page);
      params.append('limit', limit);

      const response = await fetch(`${API_BASE_URL}/forum/posts?${params}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch forum posts');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get single post with comments
  static async getPost(postId) {
    try {
      const token = localStorage.getItem('moneysmart-token');

      const response = await fetch(`${API_BASE_URL}/forum/posts/${postId}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch forum post');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Create post
  static async createPost(title, content, category = 'general') {
    try {
      const token = localStorage.getItem('moneysmart-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/forum/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title, content, category })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create forum post');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Update post
  static async updatePost(postId, title, content, category) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/forum/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title, content, category })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update forum post');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Delete post
  static async deletePost(postId) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/forum/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete forum post');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Like/unlike post
  static async likePost(postId) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/forum/posts/${postId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to like post');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Create comment
  static async createComment(postId, content) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/forum/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create comment');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Like/unlike comment
  static async likeComment(commentId) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/forum/comments/${commentId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to like comment');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Delete comment
  static async deleteComment(commentId) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/forum/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete comment');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Mark post as contribution (admin only)
  static async markAsContribution(postId) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/forum/posts/${postId}/contribution`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to mark post as contribution');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get contributions leaderboard
  static async getLeaderboard(limit = 10) {
    try {
      const token = localStorage.getItem('moneysmart-token');
      const params = new URLSearchParams();
      params.append('limit', limit);

      const response = await fetch(`${API_BASE_URL}/forum/leaderboard?${params}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

export default ForumService;

